import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Typography, Card, Table, InputNumber, Button, Space, message, Divider, Tag, Tooltip,
} from 'antd';
import { SaveOutlined, UndoOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

const { Title, Text, Paragraph } = Typography;

// Hard-coded defaults — mirrors AgentGradeCard.jsx BUCKETS exactly.
// Used when the DB table is empty (before the first save) and by "Reset to Defaults".
const DEFAULT_ROWS = [
  { BucketKey: 'call_handling', BucketLabel: 'Call Handling',      BucketWeight: 1, BucketOrder: 0, MetricKey: 'AvgHTSecs',            MetricLabel: 'Inbound Avg Handle Time',       MetricSource: 'campaign', MetricWeight: 1,    MetricOrder: 0 },
  { BucketKey: 'quality',       BucketLabel: 'Quality',            BucketWeight: 1, BucketOrder: 1, MetricKey: 'AvgQAScore',            MetricLabel: 'QA Score',              MetricSource: 'campaign', MetricWeight: 0.25, MetricOrder: 1 },
  { BucketKey: 'quality',       BucketLabel: 'Quality',            BucketWeight: 1, BucketOrder: 1, MetricKey: 'ErrorCount',            MetricLabel: 'Errors',                MetricSource: 'campaign', MetricWeight: 0.20, MetricOrder: 2 },
  { BucketKey: 'quality',       BucketLabel: 'Quality',            BucketWeight: 1, BucketOrder: 1, MetricKey: 'RecoachingCount',       MetricLabel: 'Recoach Sessions',      MetricSource: 'overall',  MetricWeight: 0.15, MetricOrder: 3 },
  { BucketKey: 'adherence',     BucketLabel: 'Schedule Adherence', BucketWeight: 1, BucketOrder: 2, MetricKey: 'AdherencePct',          MetricLabel: 'Adherence %',           MetricSource: 'overall',  MetricWeight: 0.6,  MetricOrder: 0 },
  { BucketKey: 'adherence',     BucketLabel: 'Schedule Adherence', BucketWeight: 1, BucketOrder: 2, MetricKey: 'ShortTermNoticeCount',  MetricLabel: 'Absence Notifications', MetricSource: 'overall',  MetricWeight: 0.4,  MetricOrder: 1 },
  { BucketKey: 'ccaas',         BucketLabel: 'Compliance',         BucketWeight: 1, BucketOrder: 3, MetricKey: 'ComplianceCount',       MetricLabel: 'Compliance Incidents',  MetricSource: 'overall',  MetricWeight: 0.70, MetricOrder: 0 },
  { BucketKey: 'ccaas',         BucketLabel: 'Compliance',         BucketWeight: 1, BucketOrder: 3, MetricKey: 'CCaaSUnproductivePct',  MetricLabel: 'Unproductive %',        MetricSource: 'overall',  MetricWeight: 0.15, MetricOrder: 1 },
  { BucketKey: 'ccaas',         BucketLabel: 'Compliance',         BucketWeight: 1, BucketOrder: 3, MetricKey: 'CCaaSUnavailablePct',   MetricLabel: 'Unavailable %',         MetricSource: 'overall',  MetricWeight: 0.15, MetricOrder: 2 },
];

// Group flat rows into buckets for rendering.
// Coerce weights to Number — the API may return Decimal as string after a DB round-trip.
function groupByBucket(rows) {
  const buckets = [];
  const seen = {};
  for (const r of rows) {
    if (!seen[r.BucketKey]) {
      seen[r.BucketKey] = { key: r.BucketKey, label: r.BucketLabel, weight: Number(r.BucketWeight), order: r.BucketOrder, metrics: [] };
      buckets.push(seen[r.BucketKey]);
    }
    seen[r.BucketKey].metrics.push({ ...r, MetricWeight: Number(r.MetricWeight) });
  }
  return buckets.sort((a, b) => a.order - b.order);
}

// Flatten bucket state back to rows for API
function flattenBuckets(buckets) {
  const rows = [];
  for (const b of buckets) {
    for (const m of b.metrics) {
      rows.push({
        BucketKey: b.key,
        BucketLabel: b.label,
        BucketWeight: b.weight,
        BucketOrder: b.order,
        MetricKey: m.MetricKey,
        MetricLabel: m.MetricLabel,
        MetricSource: m.MetricSource,
        MetricWeight: m.MetricWeight,
        MetricOrder: m.MetricOrder,
      });
    }
  }
  return rows;
}

function totalWeight(items) {
  return items.reduce((s, x) => s + (x.weight ?? x.MetricWeight ?? 0), 0);
}

export default function GradeWeightsAdminPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin';
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/grade-weights');
      const rows = res.data.length ? res.data : DEFAULT_ROWS;
      setBuckets(groupByBucket(rows));
      setDirty(false);
    } catch {
      message.error('Failed to load grade weights.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setBucketWeight = (bucketKey, val) => {
    setBuckets(prev => prev.map(b =>
      b.key === bucketKey ? { ...b, weight: val ?? 0 } : b,
    ));
    setDirty(true);
  };

  const setMetricWeight = (bucketKey, metricKey, val) => {
    setBuckets(prev => prev.map(b => {
      if (b.key !== bucketKey) return b;
      return {
        ...b,
        metrics: b.metrics.map(m =>
          m.MetricKey === metricKey ? { ...m, MetricWeight: val ?? 0 } : m,
        ),
      };
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/api/admin/grade-weights', flattenBuckets(buckets));
      message.success('Grade weights saved.');
      setDirty(false);
    } catch {
      message.error('Failed to save grade weights.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setBuckets(groupByBucket(DEFAULT_ROWS));
    setDirty(true);
  };

  const bucketTotal = totalWeight(buckets);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>Agent Grade Weights</Title>
        {isAdmin && (
          <Space>
            <Button icon={<UndoOutlined />} onClick={handleReset} disabled={loading}>
              Reset to Defaults
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!dirty || loading}
            >
              Save
            </Button>
          </Space>
        )}
      </div>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Configure how the agent grade scorecard weighs each bucket and metric.
        Weights within a bucket are relative — missing-data metrics are excluded and the remaining weights are renormalized automatically.
        Bucket weights determine each bucket's share of the overall score.
      </Paragraph>

      {buckets.map((bucket) => {
        const mTotal = totalWeight(bucket.metrics.map(m => ({ weight: m.MetricWeight })));
        const bucketShare = bucketTotal > 0 ? ((bucket.weight / bucketTotal) * 100).toFixed(1) : '0.0';

        return (
          <Card
            key={bucket.key}
            size="small"
            style={{ marginBottom: 16 }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Text strong>{bucket.label}</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>% of overall:</Text>
                  <InputNumber
                    size="small"
                    min={0}
                    max={100}
                    step={1}
                    precision={1}
                    formatter={(v) => `${v}%`}
                    parser={(v) => v.replace('%', '')}
                    value={parseFloat(bucketShare)}
                    onChange={isAdmin ? (v) => setBucketWeight(bucket.key, v ?? 0) : undefined}
                    disabled={!isAdmin}
                    style={{ width: 90 }}
                  />
                </div>
              </div>
            }
          >
            <Table
              rowKey="MetricKey"
              dataSource={bucket.metrics}
              size="small"
              pagination={false}
              columns={[
                {
                  title: 'Metric',
                  dataIndex: 'MetricLabel',
                  key: 'MetricLabel',
                },
                {
                  title: 'Source',
                  dataIndex: 'MetricSource',
                  key: 'MetricSource',
                  width: 100,
                  render: (v) => <Tag color={v === 'campaign' ? 'geekblue' : 'purple'}>{v}</Tag>,
                },
                {
                  title: (
                    <Space size={4}>
                      Weight
                      <Tooltip title="Relative weight within this bucket. Decimals OK — they are renormalized automatically.">
                        <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                      </Tooltip>
                    </Space>
                  ),
                  dataIndex: 'MetricWeight',
                  key: 'MetricWeight',
                  width: 110,
                  render: (val, rec) => (
                    <InputNumber
                      size="small"
                      min={0}
                      max={100}
                      step={0.05}
                      value={val}
                      onChange={isAdmin ? (v) => setMetricWeight(bucket.key, rec.MetricKey, v) : undefined}
                      disabled={!isAdmin}
                      style={{ width: 90 }}
                    />
                  ),
                },
                {
                  title: '% of bucket',
                  key: 'pct',
                  width: 110,
                  render: (_, rec) => {
                    if (!mTotal) return '—';
                    const pct = ((rec.MetricWeight / mTotal) * 100).toFixed(1);
                    return <Text type="secondary">{pct}%</Text>;
                  },
                },
              ]}
            />
          </Card>
        );
      })}

      <Divider />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Grade scale: A &ge; 80th pctl &nbsp;|&nbsp; B &ge; 65th &nbsp;|&nbsp; C &ge; 50th &nbsp;|&nbsp; D &ge; 35th &nbsp;|&nbsp; F &lt; 35th
        </Text>
        {isAdmin && (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!dirty || loading}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}
